import type { CaseData } from "@/cms/models";
import { getCaseClassification, isRental } from "./case-label";
import {
  isLand,
  isCommercial,
  isInvestmentProperty,
  isHomeSale,
  isOwnProperty,
  isAgricultural,
  isCountryEstate,
  isVillaRaekkehusOrFritidshus,
} from "./case-classification";
import { formatNumber, squareMetersToHectares } from "./number-format";

export interface CompactPropertyFact {
  key: string;
  label: string;
  value: string | null;
  unit?: "m²" | "ha";
}

export interface CompactPropertyFacts {
  family:
    | "land"
    | "investment"
    | "commercial-sale"
    | "commercial-rental"
    | "agriculture"
    | "commercial"
    | "residential";
  facts: CompactPropertyFact[];
  emptyRow: boolean;
}

// This is the Gallery card's existing policy, including branch order and omitted rows.
export function getCompactPropertyFacts(
  property: CaseData,
): CompactPropertyFacts {
  const classifications = getCaseClassification(property);
  const estate: CompactPropertyFact = {
    key: "estateType",
    label: "Ejendomstype",
    value: property.estateType || null,
  };
  const area = (
    key: "floorArea" | "housingArea" | "plotArea",
    label: string,
  ): CompactPropertyFact => ({
    key,
    label,
    value: property[key] > 0 ? formatNumber(property[key]) : null,
    unit: "m²",
  });
  const floor = area("floorArea", "Etageareal");
  const housing = area("housingArea", "Boligareal");
  const plot = area("plotArea", "Grundareal");
  const result = (
    family: CompactPropertyFacts["family"],
    facts: CompactPropertyFact[],
    emptyRow = false,
  ): CompactPropertyFacts => ({ family, facts: [...facts, estate], emptyRow });
  const operating: CompactPropertyFact = {
    key: "operatingCostsAnnually",
    label: "Driftsudgifter",
    value:
      property.operatingCostsAnnually > 0
        ? formatNumber(property.operatingCostsAnnually)
        : null,
  };

  if (isLand(classifications))
    return result("land", property.plotArea > 0 ? [plot] : [], true);
  if (isCommercial(classifications) && isInvestmentProperty(classifications))
    return result("investment", [
      floor,
      {
        key: "rateOfReturn",
        label: "Afkast i %",
        value:
          property.rateOfReturn > 0
            ? formatNumber(property.rateOfReturn, { fragments: 1 })
            : null,
      },
    ]);
  if (
    isHomeSale(classifications) &&
    isCommercial(classifications) &&
    isOwnProperty(classifications)
  )
    return result("commercial-sale", [floor, operating]);
  if (
    isRental(classifications) &&
    isCommercial(classifications) &&
    isOwnProperty(classifications)
  )
    return result("commercial-rental", [floor, operating]);
  if (isAgricultural(classifications) || isCountryEstate(classifications))
    return result("agriculture", [
      housing,
      ...(property.plotArea > 0
        ? [
            {
              ...plot,
              value: String(squareMetersToHectares(property.plotArea)),
              unit: "ha" as const,
            },
          ]
        : []),
    ]);
  if (isCommercial(classifications))
    return result("commercial", [floor, operating]);

  const showPlot = isVillaRaekkehusOrFritidshus(classifications);
  const middle: CompactPropertyFact | undefined = showPlot
    ? property.plotArea > 0
      ? plot
      : undefined
    : {
        key: "numberOfRooms",
        label: "Værelser",
        value:
          property.numberOfRooms > 0
            ? formatNumber(property.numberOfRooms)
            : null,
      };
  return result("residential", [housing, ...(middle ? [middle] : [])], !middle);
}
